import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { LOGO_URL, BRAND_NAME } from "@/lib/brand";
import { Calendar, X, ChevronLeft, ChevronRight, ImageIcon, Heart, ThumbsUp, PlayCircle } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const resolveUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

const HEART_KEY = "50ss:album:hearted";
const THUMB_KEY = "50ss:album:thumbed";

const readSet = (key) => {
  try { return new Set(JSON.parse(localStorage.getItem(key) || "[]")); }
  catch (e) { console.warn(`[album] failed to read ${key}`, e); return new Set(); }
};
const writeSet = (key, set) => {
  try { localStorage.setItem(key, JSON.stringify([...set])); }
  catch (e) { console.warn(`[album] failed to write ${key} (likely quota)`, e); }
};

const dayLabel = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

/* --- video helpers --- */
const YOUTUBE_RX = /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/;
const VIMEO_RX = /vimeo\.com\/(\d+)/;

const parseVideo = (url = "") => {
  const yt = url.match(YOUTUBE_RX);
  if (yt) return { kind: "youtube", id: yt[1], embed: `https://www.youtube.com/embed/${yt[1]}`, thumb: `https://img.youtube.com/vi/${yt[1]}/hqdefault.jpg` };
  const vm = url.match(VIMEO_RX);
  if (vm) return { kind: "vimeo", id: vm[1], embed: `https://player.vimeo.com/video/${vm[1]}`, thumb: "" };
  return { kind: "file", embed: "", thumb: "" };
};

const mediaThumb = (item) => {
  if (item.media_type !== "video") return resolveUrl(item.url);
  if (item.thumb_url) return resolveUrl(item.thumb_url);
  const info = parseVideo(item.url);
  return info.thumb || "";
};

/* --- component --- */

export default function Album() {
  const [albums, setAlbums] = useState([]);
  const [items, setItems] = useState([]);
  const [activeAlbumId, setActiveAlbumId] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [hearted, setHearted] = useState(() => readSet(HEART_KEY));
  const [thumbed, setThumbed] = useState(() => readSet(THUMB_KEY));

  useEffect(() => {
    (async () => {
      try {
        const [ev, ph] = await Promise.all([api.get("/album/events"), api.get("/album/photos")]);
        const alb = ev.data.slice(0, 4);
        setAlbums(alb);
        setItems(ph.data);
        if (alb.length && !activeAlbumId) setActiveAlbumId(alb[0].id);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const albumItems = useMemo(() => {
    const filtered = activeAlbumId ? items.filter((i) => i.event_id === activeAlbumId) : items;
    return [...filtered].sort((a, b) => (b.photo_date || "").localeCompare(a.photo_date || ""));
  }, [items, activeAlbumId]);

  const activeItem = lightbox != null ? albumItems[lightbox] : null;

  const step = (delta) => setLightbox((prev) => {
    if (prev == null) return prev;
    const n = albumItems.length;
    return ((prev + delta) % n + n) % n;
  });

  useEffect(() => {
    if (lightbox == null) return;
    const handler = (e) => {
      if (e.key === "Escape") setLightbox(null);
      else if (e.key === "ArrowRight") step(1);
      else if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightbox, albumItems.length]);

  const toggleReaction = useCallback(async (itemId, kind) => {
    const setState = kind === "heart" ? setHearted : setThumbed;
    const stateSet = kind === "heart" ? hearted : thumbed;
    const key = kind === "heart" ? HEART_KEY : THUMB_KEY;
    const field = kind === "heart" ? "likes" : "thumbs_up";
    const endpointOn = kind === "heart" ? "like" : "thumbs-up";
    const endpointOff = kind === "heart" ? "unlike" : "unthumbs-up";

    const was = stateSet.has(itemId);
    const next = new Set(stateSet);
    if (was) next.delete(itemId); else next.add(itemId);
    setState(next);
    writeSet(key, next);
    setItems((prev) => prev.map((p) => (
      p.id === itemId ? { ...p, [field]: Math.max((p[field] || 0) + (was ? -1 : 1), 0) } : p
    )));
    try {
      const { data } = await api.post(`/album/photos/${itemId}/${was ? endpointOff : endpointOn}`);
      setItems((prev) => prev.map((p) => (p.id === itemId ? { ...p, [field]: data[field] } : p)));
    } catch (err) {
      console.error(`[album] reaction toggle failed (${kind})`, err);
      // rollback
      setState(stateSet);
      writeSet(key, stateSet);
      setItems((prev) => prev.map((p) => (
        p.id === itemId ? { ...p, [field]: Math.max((p[field] || 0) + (was ? 1 : -1), 0) } : p
      )));
    }
  }, [hearted, thumbed]);

  return (
    <div data-testid="album-page" className="relative">
      {/* FIXED LOGO HERO BANNER */}
      <section
        className="sticky top-[76px] z-20 border-b border-[var(--line)]"
        style={{ background: "linear-gradient(180deg, var(--forest) 0%, #06152E 100%)" }}
      >
        <div className="absolute inset-0 grain opacity-25 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-5 md:py-7 flex items-center gap-5 md:gap-8">
          <div className="relative shrink-0">
            <div className="absolute -inset-1 rounded-full bg-gradient-to-br from-[var(--terracotta)] via-[var(--sand)] to-[var(--forest)] opacity-70 blur-[2px]" />
            <img
              src={LOGO_URL}
              alt={BRAND_NAME}
              data-testid="album-hero-logo"
              className="relative w-14 h-14 md:w-20 md:h-20 rounded-full object-cover ring-2 ring-white/40 shadow-2xl"
            />
          </div>
          <div className="min-w-0">
            <div className="overline text-[var(--sand)] mb-1">Photos</div>
            <h1 className="font-serif-display text-white text-2xl md:text-4xl leading-tight">
              Moments from <em className="italic text-[var(--sand)]">the road.</em>
            </h1>
          </div>
        </div>
      </section>

      {/* ALBUM TABS */}
      {albums.length > 0 && (
        <section className="sticky top-[164px] md:top-[184px] z-10 bg-[var(--bg)] border-b border-[var(--line)]">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-3 flex gap-2 overflow-x-auto scrollbar-thin" role="tablist">
            {albums.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => setActiveAlbumId(a.id)}
                data-testid={`album-tab-${a.id}`}
                aria-selected={activeAlbumId === a.id}
                role="tab"
                className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeAlbumId === a.id
                    ? "bg-[var(--ink)] text-[var(--bg)]"
                    : "text-[var(--ink-soft)] hover:text-[var(--ink)] border border-[var(--line)] hover:bg-[var(--bg-alt)]"
                }`}
              >
                {a.name}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* GRID */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
        {albums.length === 0 ? (
          <div className="text-center py-24 text-[var(--ink-soft)]">
            <ImageIcon className="w-10 h-10 mx-auto mb-4 opacity-40" strokeWidth={1.25} />
            <p>Photos coming soon — Rick &amp; Stephanie are still on the road.</p>
          </div>
        ) : albumItems.length === 0 ? (
          <div className="text-center py-16 text-[var(--ink-soft)] text-sm">
            No photos or videos in this album yet.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {albumItems.map((item, idx) => (
              <MediaCard
                key={item.id}
                item={item}
                idx={idx}
                onOpen={() => setLightbox(idx)}
                hearted={hearted.has(item.id)}
                thumbed={thumbed.has(item.id)}
                onHeart={() => toggleReaction(item.id, "heart")}
                onThumb={() => toggleReaction(item.id, "thumb")}
              />
            ))}
          </div>
        )}
      </section>

      {/* LIGHTBOX */}
      {activeItem && (
        <div
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="album-lightbox"
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            className="absolute top-5 right-5 text-white/80 hover:text-white p-2"
            data-testid="album-lightbox-close"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
          {albumItems.length > 1 && (
            <>
              <button type="button" onClick={(e) => { e.stopPropagation(); step(-1); }}
                className="absolute left-3 md:left-8 text-white/70 hover:text-white p-3 rounded-full hover:bg-white/10"
                aria-label="Previous" data-testid="album-lightbox-prev">
                <ChevronLeft className="w-7 h-7" />
              </button>
              <button type="button" onClick={(e) => { e.stopPropagation(); step(1); }}
                className="absolute right-3 md:right-8 text-white/70 hover:text-white p-3 rounded-full hover:bg-white/10"
                aria-label="Next" data-testid="album-lightbox-next">
                <ChevronRight className="w-7 h-7" />
              </button>
            </>
          )}
          <div className="max-w-6xl w-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <LightboxMedia item={activeItem} />
            <div className="text-center mt-5 text-white/85">
              <div className="font-mono-acc text-xs tracking-wider text-white/60 uppercase">
                {dayLabel(activeItem.photo_date)}
                <span className="mx-2">·</span>
                {lightbox + 1} / {albumItems.length}
              </div>
              {activeItem.caption && (
                <p className="mt-3 italic text-sm text-white/80 max-w-2xl">&ldquo;{activeItem.caption}&rdquo;</p>
              )}
              <div className="flex items-center justify-center gap-3 mt-5">
                <ReactionPill
                  active={hearted.has(activeItem.id)}
                  count={activeItem.likes || 0}
                  onClick={(e) => { e.stopPropagation(); toggleReaction(activeItem.id, "heart"); }}
                  testid="album-lightbox-heart"
                  icon={Heart}
                  activeLabel="Loved"
                  idleLabel="Love it"
                />
                <ReactionPill
                  active={thumbed.has(activeItem.id)}
                  count={activeItem.thumbs_up || 0}
                  onClick={(e) => { e.stopPropagation(); toggleReaction(activeItem.id, "thumb"); }}
                  testid="album-lightbox-thumb"
                  icon={ThumbsUp}
                  activeLabel="Thanks"
                  idleLabel="Thumbs up"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MediaCard({ item, onOpen, hearted, thumbed, onHeart, onThumb }) {
  const thumb = mediaThumb(item);
  const isVideo = item.media_type === "video";
  return (
    <div className="group relative rounded-sm overflow-hidden bg-[var(--bg-alt)] border border-[var(--line)]" data-testid={`album-item-${item.id}`}>
      <button type="button" onClick={onOpen} className="block w-full aspect-square cursor-zoom-in relative" aria-label={`Open ${item.caption || (isVideo ? "video" : "photo")}`}>
        {thumb ? (
          <img src={thumb} alt={item.caption || ""} loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-[var(--forest)] text-white/80">
            <PlayCircle className="w-14 h-14" strokeWidth={1.25} />
          </div>
        )}
        {isVideo && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-14 h-14 rounded-full bg-black/60 flex items-center justify-center backdrop-blur-sm">
              <PlayCircle className="w-9 h-9 text-white" strokeWidth={1.5} />
            </div>
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent px-3 py-2">
          <div className="text-white text-[11px] font-mono-acc tracking-wider uppercase flex items-center gap-1">
            <Calendar className="w-3 h-3" strokeWidth={2} />
            {dayLabel(item.photo_date) || "—"}
          </div>
        </div>
      </button>

      {/* reactions: heart + thumbs-up */}
      <div className="absolute top-2 right-2 flex gap-1.5">
        <ReactionButton
          active={hearted}
          count={item.likes || 0}
          onClick={(e) => { e.stopPropagation(); onHeart(); }}
          icon={Heart}
          testid={`album-heart-${item.id}`}
          ariaLabel={hearted ? "Remove heart" : "Add heart"}
        />
        <ReactionButton
          active={thumbed}
          count={item.thumbs_up || 0}
          onClick={(e) => { e.stopPropagation(); onThumb(); }}
          icon={ThumbsUp}
          testid={`album-thumb-${item.id}`}
          ariaLabel={thumbed ? "Remove thumbs up" : "Give a thumbs up"}
        />
      </div>
    </div>
  );
}

function ReactionButton({ active, count, onClick, icon: Icon, testid, ariaLabel }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      aria-label={ariaLabel}
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full backdrop-blur-md text-xs font-medium transition-all ${
        active
          ? "bg-[var(--terracotta)] text-[var(--ink)] shadow-lg"
          : "bg-black/50 text-white hover:bg-black/70"
      }`}
    >
      <Icon className={`w-3.5 h-3.5 ${active ? "fill-[var(--ink)]" : ""}`} strokeWidth={2} />
      {count > 0 && <span>{count}</span>}
    </button>
  );
}

function ReactionPill({ active, count, onClick, testid, icon: Icon, activeLabel, idleLabel }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
        active
          ? "bg-[var(--terracotta)] text-[var(--ink)] shadow-lg"
          : "bg-white/10 text-white hover:bg-white/20 border border-white/25"
      }`}
    >
      <Icon className={`w-4 h-4 ${active ? "fill-[var(--ink)]" : ""}`} strokeWidth={2} />
      {active ? activeLabel : idleLabel}
      {count > 0 && <span className="text-xs opacity-90">· {count}</span>}
    </button>
  );
}

function LightboxMedia({ item }) {
  if (item.media_type === "video") {
    const info = parseVideo(item.url);
    if (info.kind === "youtube" || info.kind === "vimeo") {
      return (
        <div className="w-full max-w-4xl aspect-video">
          <iframe
            src={info.embed}
            title={item.caption || "Video"}
            className="w-full h-full rounded-sm"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      );
    }
    return (
      <video
        src={resolveUrl(item.url)}
        controls
        autoPlay
        className="max-h-[75vh] max-w-full rounded-sm bg-black"
        poster={item.thumb_url ? resolveUrl(item.thumb_url) : undefined}
      />
    );
  }
  return (
    <img
      src={resolveUrl(item.url)}
      alt={item.caption || "Photo"}
      className="max-h-[75vh] max-w-full object-contain rounded-sm"
    />
  );
}
